// Admin gate. Resolves the caller's admin org membership BEFORE any admin RPC fires.
// Defense in depth: if any admin RPC slipped its 42501 check, this guard already
// redirected non-admins to /app/employee.
//
// Admin status itself comes from the shared, retried, cached store in
// ./adminStatus so the guard and the RoleSwitcher can never disagree.
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrgs, clearAdminOrgs } from './adminStatus';

type AdminState =
  | { status: 'loading' }
  | { status: 'ready'; orgId: string }
  | { status: 'not_admin' }
  | { status: 'error'; message: string };

const ACTIVE_KEY = 'admin.activeOrgId';

export function getActiveAdminOrg(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
export function setActiveAdminOrg(orgId: string | null) {
  try {
    if (orgId) localStorage.setItem(ACTIVE_KEY, orgId);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* ignore */ }
}

/** Call after joining/leaving an org so the cached answer is refreshed. */
export async function invalidateAdminStatus() {
  const { data } = await supabase.auth.getUser();
  clearAdminOrgs(data.user?.id);
}

export function useAdminGuard(): AdminState {
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const { orgs, status } = useAdminOrgs(userId);

  if (authLoading || !userId) return { status: 'loading' };
  if (status === 'loading') return { status: 'loading' };
  if (status === 'error') return { status: 'error', message: 'Could not verify admin access' };
  if (status === 'not_admin' || !orgs || orgs.length === 0) return { status: 'not_admin' };

  const preferred = getActiveAdminOrg();
  const chosen = preferred && orgs.includes(preferred) ? preferred : orgs[0];
  return { status: 'ready', orgId: chosen };
}
